import { useState } from 'react'
import { Button, HiddenMenu, Input, Menu } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Icon from 'components/priority/Icon/Icon'
import Text from 'components/priority/Text/Text'
import Divider from 'components/global/Divider/Divider'
import { useRecoilState } from 'recoil'
import { additionalFamilyDraftAtom, caseValuesAtom } from 'atoms'
import { formFieldKeys, newObjects } from '@messenger/consts'
import {
  additionalFamilyRelationSuggestions,
  additionalFamilyTypes,
  applyAdditionalFamilyDraft,
  formatAdditionalFamilyEntry,
} from '@messenger/utils'
import { useTranslation } from 'react-i18next'

/**
 * Flat name + relation entry for additional family members. Rows keep the
 * same shape/fields as the old grouped-by-type UI (`newObjects.additionalFamily`
 * + type), stored under the same `additional_family` case field, so save,
 * editor, and TinyMCE flows are untouched. New entries are typed
 * 'additional_family'; the server assigns caseId. The pencil loads an
 * existing entry into the inputs; saving updates the row in place,
 * preserving its id and any non-edited fields.
 */
export const AdditionalFamilyInput = ({ width, margin }) => {
  const { t } = useTranslation()
  const [fieldValue, setFieldValue] = useRecoilState(
    caseValuesAtom(formFieldKeys.additionalFamily),
  )
  // Pending entry lives in a Recoil draft atom (not local state) so the
  // saveCase paths can flush it — Save persists a typed-but-not-Added entry.
  const [draft, setDraft] = useRecoilState(additionalFamilyDraftAtom)
  const [showSuggestions, setShowSuggestions] = useState(false)

  const name = draft?.name || ''
  const relation = draft?.relation || ''
  const editingIndex = draft?.editingIndex ?? null
  const setName = (value) => setDraft({ ...draft, name: value })
  const setRelation = (value) => setDraft({ ...draft, relation: value })

  const entries = fieldValue?.value || []
  const isEditing = editingIndex !== null

  const localizedSuggestions = additionalFamilyRelationSuggestions.map(
    (suggestion) =>
      t(`caseFormSectionsLabels.${suggestion}`, { defaultValue: suggestion }),
  )
  const trimmedRelation = relation.trim().toLowerCase()
  const relationSuggestions = trimmedRelation
    ? localizedSuggestions.filter((suggestion) =>
        suggestion.toLowerCase().includes(trimmedRelation),
      )
    : localizedSuggestions

  const handleRelationChange = (value) => {
    setRelation(value)
    setShowSuggestions(true)
  }

  const clearInputs = () => {
    setDraft(null)
  }

  const startEdit = (index) => {
    const entry = entries[index]
    setDraft({
      name: entry?.name || '',
      relation: entry?.relation || '',
      editingIndex: index,
    })
  }

  const saveEntry = () => {
    const updated = applyAdditionalFamilyDraft(
      entries,
      draft,
      (draftName, draftRelation) => ({
        ...newObjects.additionalFamily,
        name: draftName,
        relation: draftRelation,
        type: additionalFamilyTypes.additionalFamily,
      }),
    )
    if (updated === entries) return
    setFieldValue({
      ...fieldValue,
      value: updated,
    })
    clearInputs()
  }

  const removeEntry = (index) => {
    const newArr = [...entries]
    newArr.splice(index, 1)
    setFieldValue({
      ...fieldValue,
      value: newArr,
    })
    if (editingIndex === index) clearInputs()
    else if (editingIndex !== null && editingIndex > index)
      setDraft({ ...draft, editingIndex: editingIndex - 1 })
  }

  return (
    <Flex column width={width || '100%'} margin={margin || '0'}>
      {entries.map((entry, index) => (
        <Flex
          key={`${entry.id || entry.name}_${index}`}
          align="center"
          justify="space-between"
          width="100%"
          margin="0 0 12px 0"
        >
          <Text size="14px" weight={editingIndex === index ? '600' : '400'}>
            {formatAdditionalFamilyEntry(entry)}
          </Text>
          <Flex align="center">
            <Icon
              icon="pencil"
              fill="gray2"
              hoverfill="gray1"
              height="16px"
              width="16px"
              margin="0 12px 0 0"
              onClick={() => startEdit(index)}
            />
            <Icon
              icon="deleteOutline"
              fill="gray2"
              hoverfill="gray1"
              height="16px"
              width="16px"
              onClick={() => removeEntry(index)}
            />
          </Flex>
        </Flex>
      ))}
      {entries.length > 0 && <Divider fill="gray4" margin="0 0 16px" />}
      <Flex align="flex-end" width="100%" wrap="wrap">
        <Input
          label={t('caseFormSectionsLabels.name')}
          value={[name, setName]}
          width="calc(50% - 46px)"
          minWidth="120px"
          margin="0 12px 16px 0"
          onEnter={saveEntry}
        />
        <HiddenMenu
          width="calc(50% - 46px)"
          minWidth="120px"
          margin="0 12px 16px 0"
          show={[showSuggestions, setShowSuggestions]}
          parentElement={() => (
            <Input
              label={t('caseFormSectionsLabels.relation')}
              value={[relation, handleRelationChange]}
              onEnter={() => {
                setShowSuggestions(false)
                saveEntry()
              }}
              onClick={() => setShowSuggestions(true)}
            />
          )}
          hiddenElement={() =>
            relationSuggestions.length > 0 && (
              <Menu
                full
                items={relationSuggestions.map((suggestion) => ({
                  text: suggestion,
                  onClick: () => setRelation(suggestion),
                }))}
                show={[showSuggestions, setShowSuggestions]}
              />
            )
          }
        />
        <Button
          icon={isEditing ? undefined : 'plus'}
          margin="0 12px 16px 0"
          onClick={saveEntry}
        >
          {isEditing
            ? t('caseDetails.saveButton')
            : t('nestedObjectWrapperWithKeyMapping.add')}
        </Button>
        {isEditing && (
          <Button margin="0 0 16px 0" onClick={clearInputs}>
            {t('newThemeModal.cancelButton')}
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

export default AdditionalFamilyInput
